// FloorPlanCanvas.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Utilities
import { FLOOR_PLAN_ITEM_DRAG_MIME } from 'nodetypes/project/floorplan/floorPlanItemPlacement';
import {
  DEFAULT_CANVAS_HEIGHT,
  DEFAULT_CANVAS_WIDTH,
  updateRoomGeometry,
} from 'nodetypes/project/floorplan/projectFloorPlanModel';

// CSS
import './FloorPlanCanvas.css';

// Components
import Tabs from 'components/tabs/Tabs';

/**
 * FloorPlanCanvas
 *
 * Renders floors/rooms. In edit mode supports move, resize, rename, and item drop.
 *
 * @param {object} props.layout
 * @param {string|null} props.activeFloorId
 * @param {boolean} props.isEditing
 * @param {string|null} props.selectedRoomId
 * @param {Map<string, number>|object} [props.itemCountsByRoomKey]
 * @param {boolean} [props.showFloorTabs=false] - Prefer parent-owned floor tabs
 * @param {Function} props.onActiveFloorIdChange
 * @param {Function} props.onSelectedRoomIdChange
 * @param {Function} props.onLayoutChange
 * @param {Function} [props.onGeometryCommit] - Called when drag/resize ends
 * @param {Function} [props.onRenameRoom]
 * @param {Function} [props.onItemDrop] - (itemKey, room) => void
 * @returns {ReactNode}
 */
const FloorPlanCanvas = React.memo(({
  layout,
  activeFloorId,
  isEditing,
  selectedRoomId,
  itemCountsByRoomKey = null,
  showFloorTabs = false,
  onActiveFloorIdChange,
  onSelectedRoomIdChange,
  onLayoutChange,
  onGeometryCommit,
  onRenameRoom,
  onItemDrop,
}) => {
  // ----- STATE ----- //

  // Drag / resize session
  const [dragSession, setDragSession] = useState(null);

  // Inline rename draft
  const [renameDraft, setRenameDraft] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Fit scale so the square stage fills the center column (up or down from 480).
  const [fitScale, setFitScale] = useState(1);

  // ----- REFS ----- //

  const stageRef = useRef(null);
  const fitRef = useRef(null);
  const fitScaleRef = useRef(1);
  const renameInputRef = useRef(null);

  // ----- MEMOS ----- //

  const floors = Array.isArray(layout?.floors) ? layout.floors : [];
  const activeFloor = floors.find((floor) => floor.id === activeFloorId) || floors[0] || null;
  const rooms = Array.isArray(activeFloor?.rooms) ? activeFloor.rooms : [];
  // Logical square stage (480); CSS scale maps it into the available center column.
  const canvasWidth = DEFAULT_CANVAS_WIDTH;
  const canvasHeight = DEFAULT_CANVAS_HEIGHT;

  // ----- EFFECTS ----- //

  // Keep a valid active floor when layout changes.
  useEffect(() => {
    if (!floors.length) {
      if (activeFloorId) onActiveFloorIdChange?.(null);
      return;
    }
    if (!activeFloorId || !floors.some((floor) => floor.id === activeFloorId)) {
      onActiveFloorIdChange?.(floors[0].id);
    }
  }, [activeFloorId, floors, onActiveFloorIdChange]);

  // Focus rename input.
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Scale the square stage to fill the center column width (and height), past 480 when space allows.
  useEffect(() => {
    const el = fitRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const centerEl = el.closest('.project-floor-plan-center') || el;

    const updateScale = () => {
      const width = el.getBoundingClientRect().width;
      const height = centerEl.getBoundingClientRect().height;
      const available = Math.max(0, Math.min(
        width > 0 ? width : 0,
        height > 0 ? height : width,
      ));
      const next = available > 0
        ? available / DEFAULT_CANVAS_WIDTH
        : 1;
      fitScaleRef.current = next;
      setFitScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    updateScale();

    const observer = new ResizeObserver(() => {
      updateScale();
    });
    observer.observe(el);
    if (centerEl !== el) observer.observe(centerEl);
    return () => observer.disconnect();
  }, []);

  // ----- HANDLERS ----- //

  /**
   * handleFloorTabClick
   *
   * @param {string} floorId
   */
  const handleFloorTabClick = (floorId) => {
    onActiveFloorIdChange?.(floorId);
    onSelectedRoomIdChange?.(null);
    setIsRenaming(false);
  };

  const floorTabs = useMemo(
    () => floors.map((floor) => ({
      key: floor.id,
      label: floor.name || 'Floor',
      onClick: () => handleFloorTabClick(floor.id),
    })),
    [floors],
  );

  /**
   * handleRoomMouseDown
   *
   * @param {MouseEvent} event
   * @param {object} room
   * @param {string|null} handle
   */
  const handleRoomMouseDown = (event, room, handle = null) => {
    if (!isEditing) {
      onSelectedRoomIdChange?.(room.id);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onSelectedRoomIdChange?.(room.id);
    setIsRenaming(false);

    const stageRect = stageRef.current?.getBoundingClientRect();
    if (!stageRect) return;

    setDragSession({
      roomId: room.id,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      origin: {
        x: room.x,
        y: room.y,
        width: room.width,
        height: room.height,
      },
    });
  };

  /**
   * handleStageMouseDown
   *
   * Clears selection when clicking empty canvas.
   *
   * @param {MouseEvent} event
   */
  const handleStageMouseDown = (event) => {
    if (event.target !== stageRef.current) return;
    onSelectedRoomIdChange?.(null);
    setIsRenaming(false);
  };

  /**
   * handleMouseMove
   *
   * @param {MouseEvent} event
   */
  const handleMouseMove = useCallback((event) => {
    if (!dragSession || !isEditing) return;

    const scale = fitScaleRef.current || 1;
    const dx = (event.clientX - dragSession.startClientX) / scale;
    const dy = (event.clientY - dragSession.startClientY) / scale;
    const { origin, handle, roomId } = dragSession;

    let nextGeometry = {
      x: origin.x,
      y: origin.y,
      width: origin.width,
      height: origin.height,
    };

    if (!handle) {
      nextGeometry.x = origin.x + dx;
      nextGeometry.y = origin.y + dy;
    } else if (handle === 'topLeft') {
      nextGeometry.x = origin.x + dx;
      nextGeometry.y = origin.y + dy;
      nextGeometry.width = origin.width - dx;
      nextGeometry.height = origin.height - dy;
    } else if (handle === 'topRight') {
      nextGeometry.y = origin.y + dy;
      nextGeometry.width = origin.width + dx;
      nextGeometry.height = origin.height - dy;
    } else if (handle === 'bottomLeft') {
      nextGeometry.x = origin.x + dx;
      nextGeometry.width = origin.width - dx;
      nextGeometry.height = origin.height + dy;
    } else if (handle === 'bottomRight') {
      nextGeometry.width = origin.width + dx;
      nextGeometry.height = origin.height + dy;
    }

    onLayoutChange?.(updateRoomGeometry(layout, roomId, nextGeometry));
  }, [dragSession, isEditing, layout, onLayoutChange]);

  /**
   * handleMouseUp
   */
  const handleMouseUp = useCallback(() => {
    const hadSession = Boolean(dragSession);
    setDragSession(null);
    if (hadSession && typeof onGeometryCommit === 'function') {
      onGeometryCommit();
    }
  }, [dragSession, onGeometryCommit]);

  useEffect(() => {
    if (!dragSession) return undefined;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragSession, handleMouseMove, handleMouseUp]);

  /**
   * beginRename
   *
   * @param {object} room
   */
  const beginRename = (room) => {
    if (!isEditing) return;
    setRenameDraft(room.name);
    setIsRenaming(true);
    onSelectedRoomIdChange?.(room.id);
  };

  /**
   * commitRename
   */
  const commitRename = () => {
    if (!selectedRoomId || !isRenaming) return;
    onRenameRoom?.(selectedRoomId, renameDraft);
    setIsRenaming(false);
  };

  /**
   * handleRenameKeyDown
   *
   * @param {KeyboardEvent} event
   */
  const handleRenameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsRenaming(false);
    }
  };

  /**
   * readDroppedItemKey
   *
   * @param {DragEvent} event
   * @returns {string}
   */
  const readDroppedItemKey = (event) => (
    String(
      event.dataTransfer.getData(FLOOR_PLAN_ITEM_DRAG_MIME)
      || event.dataTransfer.getData('text/plain')
      || '',
    ).trim()
  );

  /**
   * handleRoomDragOver
   *
   * @param {DragEvent} event
   */
  const handleRoomDragOver = (event) => {
    if (!isEditing || typeof onItemDrop !== 'function') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  /**
   * handleRoomDrop
   *
   * @param {DragEvent} event
   * @param {object} room
   */
  const handleRoomDrop = (event, room) => {
    if (!isEditing || typeof onItemDrop !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    const itemKey = readDroppedItemKey(event);
    if (!itemKey) return;
    onItemDrop(itemKey, room);
  };

  /**
   * getRoomItemCount
   *
   * @param {string} roomId
   * @returns {number}
   */
  const getRoomItemCount = (roomId) => {
    if (!itemCountsByRoomKey) return 0;
    if (itemCountsByRoomKey instanceof Map) {
      return Number(itemCountsByRoomKey.get(roomId) || 0);
    }
    return Number(itemCountsByRoomKey[roomId] || 0);
  };

  // ----- RENDER ----- //

  if (!floors.length) {
    return (
      <div className="floor-plan-canvas-container">
        <div className="floor-plan-canvas-empty">
          {isEditing ? 'Add a floor to begin laying out rooms.' : 'Loading floor…'}
        </div>
      </div>
    );
  }

  return (
    <div className="floor-plan-canvas-container" ref={fitRef}>

      {showFloorTabs && floorTabs.length > 0 ? (

        /* FLOOR TABS */
        <div className="floor-plan-canvas-floor-tabs">
          <Tabs
            tabs={floorTabs}
            selectedKey={activeFloor?.id || floorTabs[0]?.key}
            showLabels
            layout="wrap"
          />
        </div>
      ) : null}

      {/* STAGE WRAPPER — sized to the scaled stage so layout pushes cleanly */}
      <div
        className="floor-plan-canvas-stage-wrapper"
        style={{
          height: `${canvasHeight * fitScale}px`,
          width: `${canvasWidth * fitScale}px`,
        }}
      >

        {/* STAGE */}
        <div
          ref={stageRef}
          className={`floor-plan-canvas-stage${isEditing ? ' is-editing' : ''}`}
          style={{
            height: `${canvasHeight}px`,
            transform: fitScale === 1 ? undefined : `scale(${fitScale})`,
            transformOrigin: 'top left',
            width: `${canvasWidth}px`,
          }}
          onMouseDown={handleStageMouseDown}
        >
          {rooms.map((room) => {
            const selected = selectedRoomId === room.id;
            const itemCount = getRoomItemCount(room.id);
            return (
              <div
                key={room.id}
                className={[
                  'floor-plan-canvas-room',
                  selected ? 'is-selected' : '',
                  isEditing ? 'is-editing' : '',
                ].filter(Boolean).join(' ')}
                style={{
                  height: `${room.height}px`,
                  left: `${room.x}px`,
                  top: `${room.y}px`,
                  width: `${room.width}px`,
                }}
                onDragOver={handleRoomDragOver}
                onDrop={(event) => handleRoomDrop(event, room)}
                onMouseDown={(event) => handleRoomMouseDown(event, room, null)}
                onDoubleClick={() => beginRename(room)}
              >

                {/* ROOM NAME */}
                {isEditing && selected && isRenaming ? (
                  <input
                    ref={renameInputRef}
                    className="floor-plan-canvas-room-name-input"
                    value={renameDraft}
                    onBlur={commitRename}
                    onChange={(event) => setRenameDraft(event.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                ) : (
                  <div className="floor-plan-canvas-room-name">{room.name}</div>
                )}

                {/* ROOM ITEM COUNT */}
                {itemCount > 0 ? (
                  <div className="floor-plan-canvas-room-count">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </div>
                ) : null}

                {isEditing && selected ? (
                  <>
                    {/* HANDLE */}
                    <div
                      className="floor-plan-canvas-handle top-left"
                      onMouseDown={(event) => handleRoomMouseDown(event, room, 'topLeft')}
                    />
                    <div
                      className="floor-plan-canvas-handle top-right"
                      onMouseDown={(event) => handleRoomMouseDown(event, room, 'topRight')}
                    />
                    <div
                      className="floor-plan-canvas-handle bottom-left"
                      onMouseDown={(event) => handleRoomMouseDown(event, room, 'bottomLeft')}
                    />
                    <div
                      className="floor-plan-canvas-handle bottom-right"
                      onMouseDown={(event) => handleRoomMouseDown(event, room, 'bottomRight')}
                    />
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

// Dev Tools
FloorPlanCanvas.displayName = 'FloorPlanCanvas';

export default FloorPlanCanvas;
